import { FC, Fragment } from 'react';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';
import * as tracking from 'dibs-tracking';
import { SeoLink } from 'dibs-seo/exports/SeoLink';

import HpLazyImageWrapper from '../HpLazyImageWrapper/HpLazyImageWrapper';
import { HpSharedCarousel } from '../HpSharedCarousel/HpSharedCarousel';
import { handleItemClick } from '../helpers/carouselTracking';
import ResponsiveImage from '../ResponsiveImage';
import { trackModuleLocation, trackModule } from '../helpers/moduleLocationTracking';

import { HpSharedLocationItems_viewer$data } from './__generated__/HpSharedLocationItems_viewer.graphql';
import { HpSharedLocationItems_componentModule$data } from './__generated__/HpSharedLocationItems_componentModule.graphql';

import styles from './HpSharedLocationItems.scss';

const SHOP_LOCAL_ACTION_LABEL = 'shop local';

type Props = {
    viewer: HpSharedLocationItems_viewer$data;
    componentModule: HpSharedLocationItems_componentModule$data;
    itemCount: number;
    useLazyLoadImages: boolean;
    moduleIndex: number;
    totalModules: number;
};

export const HpSharedLocationItemsComponent: FC<Props> = ({
    viewer,
    componentModule,
    itemCount,
    useLazyLoadImages,
    moduleIndex,
    totalModules,
}) => {
    const { displayName, linkData: headerLinkData, items = [] } =
        viewer?.locationItemsInfo?.[0]?.locationPage || {};
    if (!displayName || !headerLinkData || !items || items.length < itemCount) {
        return null;
    }

    const title = (
        <FormattedMessage
            id="homepage.modules.locationItems.title"
            defaultMessage="Shop {displayName} Sellers"
            values={{ displayName }}
        />
    );

    return (
        <Fragment>
            <div className={styles.headerContainer}>
                <div className={styles.title}>{title}</div>
                <SeoLink
                    className={styles.headerCta}
                    dataTn="HpSharedLocationItems-headerCta"
                    linkData={headerLinkData}
                    onClick={() => {
                        tracking.trackEvent({
                            category: 'promo interaction',
                            action: `Shop Local view all clicked`,
                            label: '[homepage]',
                        });
                        trackModule(componentModule.cmsDisplayTitle);
                    }}
                >
                    <FormattedMessage
                        id="homepage.modules.locationItems.viewMoreCta"
                        defaultMessage="View All"
                    />
                </SeoLink>
            </div>
            <HpSharedCarousel actionLabel={SHOP_LOCAL_ACTION_LABEL} items={items}>
                {items.slice(0, itemCount).map((item, index) => {
                    if (!item) {
                        return null;
                    }
                    const { serviceId, linkData, title: itemTitle } = item;
                    if (!serviceId || !linkData) {
                        return null;
                    }

                    return (
                        <div className={styles.itemWrapper} key={serviceId}>
                            <SeoLink
                                className={styles.headerCta}
                                dataTn={`HpSharedLocationItems-${serviceId}`}
                                linkData={linkData}
                                onClick={() => {
                                    handleItemClick({
                                        items: [item],
                                        index,
                                        actionLabel: SHOP_LOCAL_ACTION_LABEL,
                                    });
                                    trackModuleLocation({
                                        label: 'shop local module',
                                        moduleIndex,
                                        totalModules,
                                    });
                                    trackModule(componentModule.cmsDisplayTitle);
                                }}
                                ariaLabel={itemTitle || undefined}
                            >
                                {item.firstPhotoWebPath && (
                                    <HpLazyImageWrapper useLazyLoad={useLazyLoadImages}>
                                        <ResponsiveImage
                                            className={styles.image}
                                            src={item.firstPhotoWebPath}
                                            srcSetWidths={[150, 240]}
                                            ariaHidden
                                            altText={itemTitle || undefined}
                                        />
                                    </HpLazyImageWrapper>
                                )}
                            </SeoLink>
                        </div>
                    );
                })}
            </HpSharedCarousel>
        </Fragment>
    );
};

export const HpSharedLocationItems = createFragmentContainer(HpSharedLocationItemsComponent, {
    viewer: graphql`
        fragment HpSharedLocationItems_viewer on Viewer {
            locationItemsInfo: regionalInfo(regionTypes: "ITEM_LOCATION", zipCode: $zipCode)
                @include(if: $isClient) {
                locationPage(
                    minCount: 1
                    maxWeight: METROPOLITAN
                    minWeight: DISTRICT
                    usOnly: true
                ) {
                    displayName
                    linkData {
                        ...SeoLink_linkData
                    }
                    items(count: $locationItemsCount) {
                        browseUrl
                        categoryCode
                        serviceId
                        contemporaryTrackingString
                        firstPhotoWebPath(size: master)
                        linkData {
                            ...SeoLink_linkData
                        }
                        seller {
                            serviceId
                        }
                        title
                    }
                }
            }
        }
    `,
    componentModule: graphql`
        fragment HpSharedLocationItems_componentModule on LocationsModule {
            cmsDisplayTitle
        }
    `,
});
